/*
 * Mimosa — colour siblings v2.2.3 (theme-only).
 *
 * v2.2 adds video indicators at no extra network cost:
 *  - Collection cards: a small translucent "play" badge on products that
 *    have a video (detected from the same /products/<handle>.js lookup
 *    the colour dots already make — no additional requests).
 *  - Product page: a floating translucent "Video" chip over the gallery
 *    when the product has a video; tapping it jumps to and plays it.
 * v2.2.1: products whose SKU base was already cached from an earlier visit
 *  were never re-fetched, so their video flag stayed unknown. Fixed.
 * v2.2.2: card badge moved to top-right; chip anchored to the main media box.
 * v2.2.3: Instagram-style buttons — card badge is a small white rounded
 *  square with a play icon (top-right); product chip is a large translucent
 *  cream circle centred on the photo, image visible through it.
 */
(function () {
  'use strict';

  var SKU_BASE_RE = /^([A-Za-z]{1,4}\d{3,})/;

  var COLOUR_WORDS = [
    'grey', 'gray', 'maroon', 'teal', 'mustard', 'red', 'green', 'blue',
    'navy', 'pink', 'yellow', 'orange', 'purple', 'violet', 'black', 'white',
    'cream', 'beige', 'gold', 'golden', 'silver', 'peach', 'wine', 'magenta',
    'rani', 'gajari', 'rama', 'mehendi', 'mehandi', 'olive', 'brown',
    'copper', 'turquoise', 'sky', 'lavender', 'coral', 'mint', 'rust',
    'onion', 'peacock', 'bottle', 'sea', 'lemon', 'ivory', 'saffire',
    'sapphire', 'off', 'light', 'dark', 'deep', 'pastel', 'bright', 'baby',
    'royal', 'hot', 'multi', 'multicolour', 'multicolor', 'firozi', 'chiku',
    'grape'
  ];
  var COLOUR_SET = {};
  COLOUR_WORDS.forEach(function (w) { COLOUR_SET[w] = true; });

  function colourFromTitle(title) {
    var words = (title || '').trim().split(/\s+/);
    if (words.length && words[0].toLowerCase() === 'mimosa') words.shift();
    var colour = [];
    while (words.length > 1 && COLOUR_SET[words[0].toLowerCase().replace(/[^a-z]/g, '')]) {
      colour.push(words.shift());
    }
    return colour.join(' ');
  }

  function el(tag, className, html) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (html !== undefined) node.innerHTML = html;
    return node;
  }

  var PLAY_SVG = '<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';

  /* ---------------- styles ---------------- */

  var css = document.createElement('style');
  css.textContent = '' +
    '.mimosa-siblings{border:1px solid rgba(0,0,0,.12);border-radius:8px;padding:14px;margin:16px 0;background:#fffdf9}' +
    '.mimosa-siblings__head{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:10px}' +
    '.mimosa-siblings__title{font-size:12px;font-weight:600;letter-spacing:.04em}' +
    '.mimosa-siblings__count{font-size:11px;opacity:.55}' +
    '.mimosa-siblings__row{display:flex;gap:10px;overflow-x:auto;padding-bottom:2px;-webkit-overflow-scrolling:touch}' +
    '.mimosa-swatch{flex:none;width:58px;text-align:center;text-decoration:none;color:inherit;cursor:pointer}' +
    '.mimosa-swatch__chip{display:block;width:58px;height:72px;border-radius:6px;border:1px solid rgba(0,0,0,.08);background-size:cover;background-position:center top;margin-bottom:5px}' +
    '.mimosa-swatch--current .mimosa-swatch__chip{outline:2px solid currentColor;outline-offset:2px}' +
    '.mimosa-swatch__name{display:block;font-size:10px;line-height:1.3;opacity:.75;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '.mimosa-swatch--current .mimosa-swatch__name{font-weight:600;opacity:1}' +
    '.mimosa-card-dots{display:flex;align-items:center;gap:5px;margin-top:6px;min-height:16px}' +
    '.mimosa-card-dots__dot{width:14px;height:14px;border-radius:50%;border:1px solid rgba(0,0,0,.15);background-size:cover;background-position:center 20%;flex:none}' +
    '.mimosa-card-dots__more{font-size:10px;opacity:.6;line-height:1}' +
    '.mimosa-video-badge{position:absolute;right:8px;top:8px;z-index:2;display:flex;align-items:center;justify-content:center;' +
      'width:30px;height:30px;border-radius:9px;background:rgba(255,255,255,.92);color:#1a1a1a;' +
      'pointer-events:none;box-shadow:0 1px 6px rgba(0,0,0,.18)}' +
    '.mimosa-video-badge svg{width:14px;height:14px}' +
    '.mimosa-video-chip{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:3;' +
      'display:flex;align-items:center;justify-content:center;width:72px;height:72px;border:0;border-radius:50%;' +
      'background:rgba(250,244,232,.88);color:#2b2317;cursor:pointer;' +
      'box-shadow:0 4px 20px rgba(0,0,0,.28);backdrop-filter:blur(2px);transition:transform .15s ease,background .15s ease}' +
    '.mimosa-video-chip svg{width:30px;height:30px;margin-left:4px}' +
    '.mimosa-video-chip:active{transform:translate(-50%,-50%) scale(.94);background:rgba(250,244,232,.97)}' +
    '@media (max-width:768px){.mimosa-video-chip{width:62px;height:62px}.mimosa-video-chip svg{width:26px;height:26px}}';
  document.head.appendChild(css);

  /* ---------------- SKU base + video lookup with cache + queue ---------------- */

  var memCache = {};
  var vidCache = {};

  function cachedBase(handle) {
    if (handle in memCache) return memCache[handle];
    var v = null;
    try { v = sessionStorage.getItem('mimosa-skub:' + handle); } catch (e) { /* ignore */ }
    if (v !== null) { memCache[handle] = v || null; return memCache[handle]; }
    return undefined;
  }

  function storeBase(handle, base) {
    memCache[handle] = base || null;
    try { sessionStorage.setItem('mimosa-skub:' + handle, base || ''); } catch (e) { /* ignore */ }
  }

  function cachedVideo(handle) {
    if (handle in vidCache) return vidCache[handle];
    var v = null;
    try { v = sessionStorage.getItem('mimosa-vid:' + handle); } catch (e) { /* ignore */ }
    if (v !== null) { vidCache[handle] = (v === '1'); return vidCache[handle]; }
    return undefined;
  }

  function storeVideo(handle, has) {
    vidCache[handle] = !!has;
    try { sessionStorage.setItem('mimosa-vid:' + handle, has ? '1' : '0'); } catch (e) { /* ignore */ }
  }

  function mediaHasVideo(media) {
    if (!media || !media.length) return false;
    for (var i = 0; i < media.length; i++) {
      var t = media[i] && media[i].media_type;
      if (t === 'video' || t === 'external_video') return true;
    }
    return false;
  }

  var queue = [];
  var queued = {};
  var active = 0;
  var MAX_ACTIVE = 4;
  var onResolved = null;
  var pumpStarted = false;

  function startPumpWhenIdle() {
    function go() {
      pumpStarted = true;
      pump();
    }
    if (document.readyState === 'complete') {
      setTimeout(go, 600);
    } else {
      window.addEventListener('load', function () { setTimeout(go, 600); });
    }
  }
  startPumpWhenIdle();

  function enqueue(handle) {
    if (queued[handle]) return;
    if (cachedBase(handle) !== undefined && cachedVideo(handle) !== undefined) return;
    queued[handle] = true;
    queue.push(handle);
    pump();
  }

  function pump() {
    if (!pumpStarted) return;
    while (active < MAX_ACTIVE && queue.length) {
      (function (handle) {
        active++;
        fetch('/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            var sku = (p && p.variants && p.variants[0] && p.variants[0].sku) || '';
            var m = sku.match(SKU_BASE_RE);
            storeBase(handle, m ? m[1].toUpperCase() : '');
            storeVideo(handle, p && mediaHasVideo(p.media));
          })
          .catch(function () { storeBase(handle, ''); storeVideo(handle, false); })
          .then(function () {
            active--;
            if (onResolved) onResolved();
            pump();
          });
      })(queue.shift());
    }
  }

  /* ---------------- product page ---------------- */

  function productPage() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    var product = meta && meta.product;
    if (!product || !product.variants || !product.variants.length) return;

    var sku = product.variants[0].sku || '';
    var m = sku.match(SKU_BASE_RE);
    if (!m) return;
    var base = m[1].toUpperCase();
    var currentHandle = (location.pathname.match(/\/products\/([^\/?]+)/) || [])[1];

    var cacheKey = 'mimosa-sib-' + base;
    var cached = null;
    try { cached = JSON.parse(sessionStorage.getItem(cacheKey)); } catch (e) { /* ignore */ }
    if (cached) { render(cached); return; }

    var url = '/search/suggest.json?q=' + encodeURIComponent(base) +
      '&resources[type]=product&resources[limit]=10' +
      '&resources[options][unavailable_products]=hide' +
      '&resources[options][fields]=variants.sku';

    fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        var products = (((data.resources || {}).results || {}).products) || [];
        var siblings = products
          .filter(function (p) {
            var handle = (p.url.match(/\/products\/([^\/?]+)/) || [])[1];
            return handle && handle !== currentHandle;
          })
          .map(function (p) {
            return {
              url: p.url.split('?')[0],
              image: (p.featured_image && p.featured_image.url) || p.image || '',
              colour: colourFromTitle(p.title) || p.title.replace(/^mimosa\s+/i, '').split(' ')[0]
            };
          });
        try { sessionStorage.setItem(cacheKey, JSON.stringify(siblings)); } catch (e) { /* ignore */ }
        render(siblings);
      })
      .catch(function () { /* silent */ });

    function render(siblings) {
      if (!siblings || !siblings.length) return;
      if (document.querySelector('.mimosa-siblings')) return;

      var block = el('div', 'mimosa-siblings');
      var total = siblings.length + 1;
      block.appendChild(el('div', 'mimosa-siblings__head',
        '<span class="mimosa-siblings__title">Also available in</span>' +
        '<span class="mimosa-siblings__count">' + total + ' colours</span>'));

      var row = el('div', 'mimosa-siblings__row');

      var og = document.querySelector('meta[property="og:image"]');
      var ownTitle = (product.variants[0].name || document.title).split(' - ')[0];
      var own = el('span', 'mimosa-swatch mimosa-swatch--current');
      own.innerHTML =
        '<span class="mimosa-swatch__chip"' + (og ? ' style="background-image:url(' + og.content + ')"' : '') + '></span>' +
        '<span class="mimosa-swatch__name">' + (colourFromTitle(ownTitle) || 'This colour') + '</span>';
      row.appendChild(own);

      siblings.forEach(function (s) {
        var a = el('a', 'mimosa-swatch');
        a.href = s.url;
        a.innerHTML =
          '<span class="mimosa-swatch__chip" style="background-image:url(' + s.image + ')"></span>' +
          '<span class="mimosa-swatch__name">' + s.colour + '</span>';
        a.addEventListener('click', function (e) {
          e.preventDefault();
          location.replace(s.url);
        });
        row.appendChild(a);
      });
      block.appendChild(row);

      var anchor = document.querySelector('.product-info .price, .product__info .price, .price');
      var form = document.querySelector('form[action*="/cart/add"]');
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(block, anchor.nextSibling);
      } else if (form && form.parentNode) {
        form.parentNode.insertBefore(block, form);
      }
    }
  }

  /* ---------------- product page: floating "Video" chip ---------------- */

  function findSlideCounter() {
    var known = document.querySelector('[class*="slide-count" i], [class*="media-count" i]');
    if (known) return known;
    var els = document.querySelectorAll('main span, main div');
    for (var i = 0; i < els.length; i++) {
      if (els[i].children.length === 0 && /^\s*\d+\s*\/\s*\d+\s*$/.test(els[i].textContent || '')) return els[i];
    }
    return null;
  }

  function productVideoChip() {
    if (document.querySelector('.mimosa-video-chip')) return;

    var videoMedia = document.querySelector(
      'deferred-media, [data-media-type="video"], [data-media-type="external_video"], .media--video, .product__media video, .media-gallery video, .product-media video'
    );
    if (!videoMedia) return;

    var counter = findSlideCounter();
    var gallery = counter && (counter.offsetParent || counter.parentElement);
    if (!gallery) {
      gallery = document.querySelector('.media-gallery, media-gallery, .product__media-list, .product-media-container, .product__media-wrapper');
    }
    if (!gallery) return;

    var style = window.getComputedStyle(gallery);
    if (style.position === 'static') gallery.style.position = 'relative';

    var chip = el('button', 'mimosa-video-chip', PLAY_SVG);
    chip.type = 'button';
    chip.setAttribute('aria-label', 'Watch product video');
    chip.addEventListener('click', function () {
      videoMedia.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(function () {
        var playBtn = videoMedia.querySelector('button, .deferred-media__poster-button');
        if (playBtn) playBtn.click();
        else if (videoMedia.tagName === 'VIDEO') { try { videoMedia.play(); } catch (e) { /* ignore */ } }
      }, 450);
    });
    gallery.appendChild(chip);
  }

  /* ---------------- collection dots + video badges ---------------- */

  function collectionDots() {
    var grid = document.querySelector('#filter-results ul.grid, ul.product-grid, #filter-results .grid');
    if (!grid) return;

    function cardInfos() {
      return Array.prototype.slice.call(grid.children)
        .map(function (card) {
          if (!card.querySelector) return null;
          var link = card.querySelector('a[href*="/products/"]');
          if (!link) return null;
          var handle = (link.getAttribute('href').match(/\/products\/([^\/?]+)/) || [])[1];
          if (!handle) return null;
          var img = card.querySelector('img');
          return {
            card: card,
            handle: handle,
            image: img ? (img.currentSrc || img.src) : '',
            imgEl: img,
            priceEl: card.querySelector('.price, .card__price, .product-price')
          };
        })
        .filter(Boolean);
    }

    function addVideoBadge(info) {
      if (info.card.querySelector('.mimosa-video-badge')) return;
      if (!info.imgEl) return;
      var host = info.imgEl.closest('.media') || info.imgEl.parentElement;
      if (!host) return;
      var style = window.getComputedStyle(host);
      if (style.position === 'static') host.style.position = 'relative';
      host.appendChild(el('span', 'mimosa-video-badge', PLAY_SVG));
    }

    function annotate() {
      var infos = cardInfos();
      var groups = {};
      var lookAhead = window.innerHeight + 600;
      infos.forEach(function (info) {
        var base = cachedBase(info.handle);
        var vid = cachedVideo(info.handle);
        if (base === undefined || vid === undefined) {
          var rect = info.card.getBoundingClientRect();
          if (rect.top < lookAhead && rect.bottom > -300) enqueue(info.handle);
          if (base === undefined) return;
        }
        if (vid) addVideoBadge(info);
        if (!base) return;
        (groups[base] = groups[base] || []).push(info);
      });

      Object.keys(groups).forEach(function (base) {
        var group = groups[base];
        group.forEach(function (info) {
          var existing = info.card.querySelector('.mimosa-card-dots');
          if (group.length < 2) {
            if (existing) existing.parentNode.removeChild(existing);
            return;
          }
          if (existing) {
            if (parseInt(existing.getAttribute('data-n'), 10) === group.length) return;
            existing.parentNode.removeChild(existing);
          }
          var dots = el('div', 'mimosa-card-dots');
          dots.setAttribute('data-n', group.length);

          var shown = 0;
          var ownDot = el('span', 'mimosa-card-dots__dot');
          if (info.image) ownDot.style.backgroundImage = 'url(' + info.image + ')';
          dots.appendChild(ownDot); shown++;

          for (var i = 0; i < group.length && shown < 4; i++) {
            if (group[i] === info) continue;
            var d = el('span', 'mimosa-card-dots__dot');
            if (group[i].image) d.style.backgroundImage = 'url(' + group[i].image + ')';
            dots.appendChild(d); shown++;
          }
          var extra = group.length - shown;
          if (extra > 0) dots.appendChild(el('span', 'mimosa-card-dots__more', '+' + extra));

          var target = info.priceEl || info.card.firstElementChild;
          if (target) target.parentNode.insertBefore(dots, target.nextSibling);
        });
      });
    }

    var pending = null;
    function scheduleAnnotate() {
      if (pending) clearTimeout(pending);
      pending = setTimeout(annotate, 200);
    }

    onResolved = scheduleAnnotate;
    annotate();

    var observer = new MutationObserver(scheduleAnnotate);
    observer.observe(grid, { childList: true, subtree: false });

    window.addEventListener('scroll', scheduleAnnotate, { passive: true });
  }

  /* ---------------- boot ---------------- */

  function init() {
    if (document.querySelector('form[action*="/cart/add"]') && window.ShopifyAnalytics) {
      productPage();
      if (document.readyState === 'complete') {
        setTimeout(productVideoChip, 800);
      } else {
        window.addEventListener('load', function () { setTimeout(productVideoChip, 800); });
      }
    }
    collectionDots();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
